import { useEffect, useRef, useCallback, useSyncExternalStore } from 'react';
import type {
  UseSignalRResult,
  ConnectionState,
  SignalRClientMethods,
  RealTimeEventHandlers,
} from '../types/signalr';
import { SignalRService, getSignalRService, disposeSignalRService } from '../services/signalRService';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';

interface SignalRState {
  connectionState: ConnectionState;
  isConnected: boolean;
  isConnecting: boolean;
  isReconnecting: boolean;
  error?: string;
  reconnectAttempts: number;
  lastConnected?: Date;
}

class SignalRStore {
  private listeners = new Set<() => void>();
  private state: SignalRState = {
    connectionState: 'Disconnected',
    isConnected: false,
    isConnecting: false,
    isReconnecting: false,
    reconnectAttempts: 0,
  };

  private service: SignalRService | null = null;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = () => this.state;

  getServerSnapshot = () => this.state;

  private notifyListeners = () => {
    this.listeners.forEach((listener) => listener());
  };

  private updateState = (updates: Partial<SignalRState>) => {
    this.state = { ...this.state, ...updates };
    this.notifyListeners();
  };

  initializeService = (accessToken: string) => {
    if (!this.service) {
      this.service = getSignalRService(accessToken);
      this.setupEventHandlers();
    }
  };

  private setupEventHandlers = () => {
    if (!this.service) return;

    this.service.onConnectionStateChanged((connectionState) => {
      const info = this.service!.getConnectionInfo();
      this.updateState({
        connectionState,
        isConnected: info.isConnected,
        isConnecting: connectionState === 'Connecting',
        isReconnecting: connectionState === 'Reconnecting',
        error: info.error,
        reconnectAttempts: info.reconnectAttempts,
        lastConnected: info.lastConnected,
      });
    });
  };

  getService = () => this.service;

  dispose = () => {
    if (this.service) {
      disposeSignalRService();
      this.service = null;
      this.updateState({
        connectionState: 'Disconnected',
        isConnected: false,
        isConnecting: false,
        isReconnecting: false,
        error: undefined,
        reconnectAttempts: 0,
      });
    }
  };
}

// Global store instance
const signalRStore = new SignalRStore();

/**
 * Hook for managing SignalR connection and real-time events
 */
export const useSignalR = (eventHandlers?: RealTimeEventHandlers): UseSignalRResult => {
  const { isAuthenticated } = useAuth();
  const token = authService.getAccessToken();
  const state = useSyncExternalStore(
    signalRStore.subscribe,
    signalRStore.getSnapshot,
    signalRStore.getServerSnapshot
  );

  const eventHandlersRef = useRef(eventHandlers);
  eventHandlersRef.current = eventHandlers;

  // Initialize service when authenticated with valid token
  useEffect(() => {
    if (isAuthenticated && token) {
      signalRStore.initializeService(token);
    } else {
      signalRStore.dispose();
    }

    return () => {
      // Don't dispose on unmount as other components might be using it
    };
  }, [isAuthenticated, token]);

  // Register event handlers
  useEffect(() => {
    const service = signalRStore.getService();
    if (!service || !eventHandlersRef.current) return;

    const handlers = eventHandlersRef.current;
    const registeredHandlers: Array<{
      method: keyof SignalRClientMethods;
      handler: Function;
    }> = [];

    // Register all event handlers
    if (handlers.onRealTimeEvent) {
      const handler = handlers.onRealTimeEvent;
      service.on('RealTimeEvent', handler);
      registeredHandlers.push({ method: 'RealTimeEvent', handler });
    }

    if (handlers.onPresenceUpdate) {
      const handler = handlers.onPresenceUpdate;
      service.on('PresenceUpdate', handler);
      registeredHandlers.push({ method: 'PresenceUpdate', handler });
    }

    if (handlers.onTypingIndicator) {
      const handler = handlers.onTypingIndicator;
      service.on('TypingIndicator', handler);
      registeredHandlers.push({ method: 'TypingIndicator', handler });
    }

    if (handlers.onSystemNotification) {
      const handler = handlers.onSystemNotification;
      service.on('SystemNotification', handler);
      registeredHandlers.push({ method: 'SystemNotification', handler });
    }

    if (handlers.onLiveMetrics) {
      const handler = handlers.onLiveMetrics;
      service.on('LiveMetrics', handler);
      registeredHandlers.push({ method: 'LiveMetrics', handler });
    }

    if (handlers.onActivityFeedUpdate) {
      const handler = handlers.onActivityFeedUpdate;
      service.on('ActivityFeedUpdate', handler);
      registeredHandlers.push({ method: 'ActivityFeedUpdate', handler });
    }

    if (handlers.onCollaborationEvent) {
      const handler = handlers.onCollaborationEvent;
      service.on('CollaborationEvent', handler);
      registeredHandlers.push({ method: 'CollaborationEvent', handler });
    }

    if (handlers.onBulkOperationProgress) {
      const handler = handlers.onBulkOperationProgress;
      service.on('BulkOperationProgress', handler);
      registeredHandlers.push({ method: 'BulkOperationProgress', handler });
    }

    if (handlers.onConnectionStatus) {
      const handler = handlers.onConnectionStatus;
      service.on('ConnectionStatus', handler);
      registeredHandlers.push({ method: 'ConnectionStatus', handler });
    }

    if (handlers.onError) {
      const handler = handlers.onError;
      service.on('Error', handler);
      registeredHandlers.push({ method: 'Error', handler });
    }

    // Cleanup function to unregister handlers
    return () => {
      registeredHandlers.forEach(({ method, handler }) => {
        service.off(method, handler as any);
      });
    };
  }, []);

  // Connection methods
  const connect = useCallback(async () => {
    const service = signalRStore.getService();
    if (!service) {
      throw new Error('SignalR service not initialized');
    }

    try {
      await service.connect();
      eventHandlersRef.current?.onConnected?.();
    } catch (error) {
      eventHandlersRef.current?.onError?.(
        error instanceof Error ? error.message : 'Connection failed'
      );
      throw error;
    }
  }, []);

  const disconnect = useCallback(async () => {
    const service = signalRStore.getService();
    if (!service) return;

    try {
      await service.disconnect();
      eventHandlersRef.current?.onDisconnected?.();
    } catch (error) {
      eventHandlersRef.current?.onError?.(
        error instanceof Error ? error.message : 'Disconnect failed'
      );
      throw error;
    }
  }, []);

  // Hub methods
  const joinTicketGroup = useCallback(async (ticketId: string) => {
    const service = signalRStore.getService();
    if (!service) {
      throw new Error('SignalR service not initialized');
    }
    await service.joinTicketGroup(ticketId);
  }, []);

  const leaveTicketGroup = useCallback(async (ticketId: string) => {
    const service = signalRStore.getService();
    if (!service) {
      throw new Error('SignalR service not initialized');
    }
    await service.leaveTicketGroup(ticketId);
  }, []);

  const startTyping = useCallback(async (ticketId: string) => {
    const service = signalRStore.getService();
    if (!service) return;
    await service.startTyping(ticketId);
  }, []);

  const stopTyping = useCallback(async (ticketId: string) => {
    const service = signalRStore.getService();
    if (!service) return;
    await service.stopTyping(ticketId);
  }, []);

  const updatePresence = useCallback(async (status: string, currentTicketId?: string) => {
    const service = signalRStore.getService();
    if (!service) return;
    await service.updatePresence(status, currentTicketId);
  }, []);

  // Event subscription methods
  const on = useCallback(<K extends keyof SignalRClientMethods>(
    methodName: K,
    handler: SignalRClientMethods[K]
  ) => {
    const service = signalRStore.getService();
    if (!service) return;
    service.on(methodName, handler);
  }, []);

  const off = useCallback(<K extends keyof SignalRClientMethods>(
    methodName: K,
    handler?: SignalRClientMethods[K]
  ) => {
    const service = signalRStore.getService();
    if (!service) return;
    service.off(methodName, handler);
  }, []);

  return {
    ...state,
    connect,
    disconnect,
    joinTicketGroup,
    leaveTicketGroup,
    startTyping,
    stopTyping,
    updatePresence,
    on,
    off,
  };
};

/**
 * Hook for managing typing indicators
 */
export const useTypingIndicator = (ticketId?: string) => {
  const { startTyping, stopTyping, isConnected } = useSignalR();
  const typingTimeoutRef = useRef<NodeJS.Timeout>();

  const handleStartTyping = useCallback(() => {
    if (!isConnected || !ticketId) return;

    // Clear any existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Start typing
    startTyping(ticketId).catch(console.error);

    // Auto-stop typing after 5 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      stopTyping(ticketId).catch(console.error);
    }, 5000);
  }, [isConnected, ticketId, startTyping, stopTyping]);

  const handleStopTyping = useCallback(() => {
    if (!isConnected || !ticketId) return;

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    stopTyping(ticketId).catch(console.error);
  }, [isConnected, ticketId, stopTyping]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (ticketId) {
        handleStopTyping();
      }
    };
  }, [ticketId, handleStopTyping]);

  return {
    startTyping: handleStartTyping,
    stopTyping: handleStopTyping,
  };
};

/**
 * Hook for managing ticket group membership
 */
export const useTicketGroup = (ticketId?: string) => {
  const { joinTicketGroup, leaveTicketGroup, isConnected } = useSignalR();

  useEffect(() => {
    if (!isConnected || !ticketId) return;

    // Join ticket group when connected and ticket ID is available
    joinTicketGroup(ticketId).catch(console.error);

    // Leave ticket group on cleanup
    return () => {
      leaveTicketGroup(ticketId).catch(console.error);
    };
  }, [isConnected, ticketId, joinTicketGroup, leaveTicketGroup]);
};

/**
 * Hook for managing user presence
 */
export const usePresence = () => {
  const { updatePresence, isConnected } = useSignalR();

  const setPresence = useCallback((status: string, currentTicketId?: string) => {
    if (!isConnected) return;
    updatePresence(status, currentTicketId).catch(console.error);
  }, [isConnected, updatePresence]);

  // Update presence to online when connected
  useEffect(() => {
    if (isConnected) {
      setPresence('Online');
    }
  }, [isConnected, setPresence]);

  // Set presence to offline when the page is being closed
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isConnected) {
        setPresence('Offline');
      }
    };

    const handleVisibilityChange = () => {
      if (isConnected) {
        const status = document.hidden ? 'Away' : 'Online';
        setPresence(status);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isConnected, setPresence]);

  return {
    setPresence,
  };
};